import { useEffect, useState } from 'react';
import { fetchActivities, createActivity, updateActivity, deleteActivity } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, Circle, Trash2, Phone, Mail, Calendar as CalIcon, ClipboardCheck, StickyNote, Plus } from 'lucide-react';
import { toast } from 'sonner';

const TYPE_META = {
    note: { icon: StickyNote, color: 'text-slate-500', label: 'Note' },
    call: { icon: Phone, color: 'text-[#189290]', label: 'Call' },
    email: { icon: Mail, color: 'text-[#FF9F0A]', label: 'Email' },
    meeting: { icon: CalIcon, color: 'text-[#016a6d]', label: 'Meeting' },
    task: { icon: ClipboardCheck, color: 'text-[#34C759]', label: 'Task' },
    stage_change: { icon: Circle, color: 'text-slate-500', label: 'Stage Change' },
};

export default function ActivityTimeline({ leadId }) {
    const [activities, setActivities] = useState([]);
    const [form, setForm] = useState({ type: 'note', title: '', description: '', due_date: '' });
    const [adding, setAdding] = useState(false);

    const load = async () => {
        try { setActivities(await fetchActivities(leadId)); }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error('Failed to load activities:', err);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

    const add = async () => {
        if (!form.title.trim()) { toast.error('Title required'); return; }
        setAdding(true);
        try {
            const payload = { ...form };
            if (payload.due_date) {
                payload.due_date = new Date(payload.due_date).toISOString();
            } else {
                delete payload.due_date;
            }
            await createActivity(leadId, payload);
            setForm({ type: 'note', title: '', description: '', due_date: '' });
            toast.success('Activity added');
            load();
        } catch { toast.error('Failed to add activity'); }
        setAdding(false);
    };

    const toggle = async (a) => {
        try {
            await updateActivity(a.id, { completed: !a.completed });
            load();
        } catch { toast.error('Update failed'); }
    };

    const remove = async (id) => {
        if (!window.confirm('Delete this activity?')) return;
        await deleteActivity(id);
        load();
    };

    const openTasks = activities.filter(a => a.type === 'task' && !a.completed);
    const now = new Date();

    return (
        <div className="surface-1 rounded-md p-6" data-testid="activity-timeline">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <div className="overline">Activity Timeline</div>
                    <h3 className="font-display text-xl font-bold text-slate-900 mt-1">
                        {activities.length} events · <span className="text-[#FF9F0A]">{openTasks.length} open tasks</span>
                    </h3>
                </div>
            </div>

            {/* Composer */}
            <div className="bg-[#F1F5F9] border border-[#E2E8F0] rounded-md p-4 mb-6 space-y-3">
                <div className="flex gap-2 flex-wrap">
                    {Object.entries(TYPE_META).filter(([k]) => k !== 'stage_change').map(([key, meta]) => {
                        const Icon = meta.icon;
                        const active = form.type === key;
                        return (
                            <button
                                key={key}
                                onClick={() => setForm({ ...form, type: key })}
                                data-testid={`activity-type-${key}`}
                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs border transition-colors ${active
                                    ? 'bg-[#FFFFFF] border-[#016a6d] text-slate-900'
                                    : 'border-[#E2E8F0] text-slate-500 hover:text-slate-900'}`}
                            >
                                <Icon className={`w-3.5 h-3.5 ${active ? meta.color : ''}`} />
                                {meta.label}
                            </button>
                        );
                    })}
                </div>
                <Input
                    placeholder="What happened? (title)"
                    value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })}
                    data-testid="activity-title"
                    className="bg-[#F5F7FA] border-[#E2E8F0] text-slate-900 h-9"
                />
                <Textarea
                    placeholder="Details (optional)"
                    value={form.description}
                    rows={2}
                    onChange={e => setForm({ ...form, description: e.target.value })}
                    data-testid="activity-description"
                    className="bg-[#F5F7FA] border-[#E2E8F0] text-slate-900 text-sm"
                />
                <div className="flex items-center gap-2">
                    {form.type === 'task' && (
                        <Input
                            type="date"
                            value={form.due_date}
                            onChange={e => setForm({ ...form, due_date: e.target.value })}
                            data-testid="activity-due"
                            className="bg-[#F5F7FA] border-[#E2E8F0] text-slate-900 h-9 w-44"
                        />
                    )}
                    <Button
                        onClick={add}
                        disabled={adding}
                        data-testid="btn-add-activity"
                        className="ml-auto bg-[#016a6d] hover:bg-[#014e50] gap-2 h-9"
                    >
                        <Plus className="w-4 h-4" /> {adding ? 'Adding…' : 'Log Activity'}
                    </Button>
                </div>
            </div>

            {/* Timeline */}
            <div className="relative space-y-4">
                {activities.length === 0 && (
                    <div className="text-center text-sm text-slate-500 py-6">No activities yet — log the first call, email, or task above.</div>
                )}
                {activities.map((a, i) => {
                    const meta = TYPE_META[a.type] || TYPE_META.note;
                    const Icon = meta.icon;
                    const overdue = a.type === 'task' && !a.completed && a.due_date && new Date(a.due_date) < now;
                    return (
                        <div key={a.id} className="flex gap-3" data-testid={`activity-${a.id}`}>
                            {/* rail */}
                            <div className="flex flex-col items-center">
                                <div className={`w-8 h-8 rounded-full bg-[#F1F5F9] border border-[#E2E8F0] flex items-center justify-center ${meta.color}`}>
                                    <Icon className="w-4 h-4" />
                                </div>
                                {i < activities.length - 1 && <div className="w-px flex-1 bg-[#E2E8F0] my-1" />}
                            </div>
                            {/* content */}
                            <div className="flex-1 pb-2">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {a.type === 'task' && (
                                                <button onClick={() => toggle(a)} data-testid={`activity-toggle-${a.id}`} className="shrink-0">
                                                    {a.completed
                                                        ? <CheckCircle2 className="w-4 h-4 text-[#34C759]" />
                                                        : <Circle className="w-4 h-4 text-slate-500 hover:text-slate-900 transition-colors" />
                                                    }
                                                </button>
                                            )}
                                            <span className={`text-sm font-medium ${a.completed ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                                                {a.title}
                                            </span>
                                            {overdue && (
                                                <span className="text-[10px] uppercase tracking-wider text-[#FF453A] border border-[#FF453A]/40 px-1.5 py-0.5 rounded">Overdue</span>
                                            )}
                                        </div>
                                        {a.description && (
                                            <p className="text-sm text-slate-500 mt-1 whitespace-pre-line">{a.description}</p>
                                        )}
                                        <div className="flex items-center gap-3 mt-1.5 text-[10px] uppercase tracking-wider text-slate-500">
                                            <span>{meta.label}</span>
                                            <span>{formatDate(a.created_at)}</span>
                                            {a.due_date && <span className="text-[#FF9F0A]">Due {formatDate(a.due_date)}</span>}
                                            {a.completed_at && <span className="text-[#34C759]">Done {formatDate(a.completed_at)}</span>}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => remove(a.id)}
                                        data-testid={`activity-delete-${a.id}`}
                                        className="text-slate-400 hover:text-[#FF453A] transition-colors shrink-0"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
